import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Search,
  Bell,
  User,
  ChevronRight,
  LogOut,
  LayoutDashboard,
  Menu,
  X,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import type { Notification } from "@/types/database";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";

// ---------------------------------------------------------------------------
// Breadcrumb helpers
// ---------------------------------------------------------------------------

const SEGMENT_LABELS: Record<string, string> = {
  admin:         "Admin",
  dashboard:     "Dashboard",
  users:         "Users",
  buildings:     "Buildings",
  units:         "Units",
  bookings:      "Bookings",
  compliance:    "Compliance",
  "audit-logs":  "Audit Logs",
  subscriptions: "Subscriptions",
  invoices:      "Invoices",
  messages:      "Messages",
  announcements: "Announcements",
  settings:      "Settings",
  email:         "Email Config",
  features:      "Feature Flags",
};

function humanize(segment: string): string {
  if (SEGMENT_LABELS[segment]) return SEGMENT_LABELS[segment];
  if (/^[0-9a-f-]{20,}$/i.test(segment)) return "Detail";
  return segment.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function Breadcrumb({ pathname }: { pathname: string }) {
  const segments = pathname.split("/").filter(Boolean);

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm min-w-0">
      {segments.map((seg, idx) => {
        const href  = "/" + segments.slice(0, idx + 1).join("/");
        const isLast = idx === segments.length - 1;
        const label  = humanize(seg);

        // On mobile, only show the last segment
        const hiddenOnMobile = !isLast && idx < segments.length - 2;

        return (
          <span
            key={href}
            className={cn("flex items-center gap-1 min-w-0", hiddenOnMobile && "hidden sm:flex")}
          >
            {idx > 0 && (
              <ChevronRight className={cn("h-3.5 w-3.5 text-gray-300 shrink-0", hiddenOnMobile && "hidden sm:block")} />
            )}
            {isLast ? (
              <span className="font-semibold text-gray-900 truncate">{label}</span>
            ) : (
              <Link
                to={href}
                className="text-gray-500 hover:text-gray-700 transition-colors truncate hidden sm:inline"
              >
                {label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Notifications dropdown
// ---------------------------------------------------------------------------

function NotificationItem({ n }: { n: Notification }) {
  let relTime = "";
  try { relTime = formatDistanceToNow(new Date(n.created_at), { addSuffix: true }); } catch { /* ignore */ }

  return (
    <div className={cn("px-4 py-3 hover:bg-gray-50 transition-colors", !n.read && "border-l-2 border-blue-500 pl-[14px]")}>
      <p className={cn("text-sm leading-snug", !n.read ? "font-medium text-gray-900" : "text-gray-700")}>
        {n.title}
      </p>
      <p className="text-xs text-gray-400 mt-0.5">{relTime}</p>
    </div>
  );
}

function NotificationBell() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading]   = useState(false);
  const [open, setOpen]         = useState(false);
  const unreadCount = notifications.filter((n) => !n.read).length;

  async function fetchNotifications() {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(5);
      setNotifications((data as Notification[]) ?? []);
    } catch { /* silent */ }
    setLoading(false);
  }

  useEffect(() => { if (open) fetchNotifications(); }, [open]); // eslint-disable-line

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9" aria-label="Notifications">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-red-500" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-900">Notifications</p>
          {unreadCount > 0 && <p className="text-xs text-gray-500">{unreadCount} unread</p>}
        </div>
        <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="px-4 py-3 space-y-1.5">
                  <Skeleton className="h-3.5 w-3/4" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              ))
            : notifications.length === 0
            ? <div className="px-4 py-8 text-center text-sm text-gray-400">No notifications</div>
            : notifications.map((n) => <NotificationItem key={n.id} n={n} />)
          }
        </div>
        <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50">
          <Link
            to="/admin/notifications"
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
            onClick={() => setOpen(false)}
          >
            View all notifications
          </Link>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// User menu
// ---------------------------------------------------------------------------

function UserMenu() {
  const { user, profile, logout } = useAuth();
  const navigate = useNavigate();

  const displayName = profile?.full_name ?? user?.fullName ?? "Admin";
  const email       = profile?.email     ?? user?.email    ?? "";
  const avatarUrl   = profile?.avatar_url ?? user?.avatarUrl;
  const initials    = displayName.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() ?? "").join("");

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-100 transition-colors"
          aria-label="User menu"
        >
          <Avatar className="h-7 w-7">
            {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName} />}
            <AvatarFallback className="text-xs bg-blue-100 text-blue-700 font-semibold">
              {initials || <User className="h-3 w-3" />}
            </AvatarFallback>
          </Avatar>
          {/* Name hidden on small screens to save space */}
          <span className="text-sm font-medium text-gray-700 max-w-[120px] truncate hidden sm:block">
            {displayName}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-3 py-2">
          <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
          <p className="text-xs text-gray-500 truncate">{email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/dashboard")}>
          <LayoutDashboard className="mr-2 h-4 w-4" />
          Back to App
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Mobile search (expands inline when tapped)
// ---------------------------------------------------------------------------

function MobileSearch() {
  const navigate  = useNavigate();
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const inputRef  = useRef<HTMLInputElement>(null);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && query.trim()) {
      navigate(`/admin/users?search=${encodeURIComponent(query.trim())}`);
      setOpen(false);
      setQuery("");
    }
    if (e.key === "Escape") { setOpen(false); setQuery(""); }
  }

  if (open) {
    return (
      <div className="flex items-center gap-2 flex-1">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search…"
            className="pl-9 h-9 text-sm bg-gray-50 border-gray-200 focus:bg-white w-full"
          />
        </div>
        <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => { setOpen(false); setQuery(""); }}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <Button variant="ghost" size="icon" className="h-9 w-9" aria-label="Search" onClick={() => setOpen(true)}>
      <Search className="h-4 w-4" />
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Desktop search
// ---------------------------------------------------------------------------

function DesktopSearch() {
  const navigate  = useNavigate();
  const [query, setQuery] = useState("");

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && query.trim()) {
      navigate(`/admin/users?search=${encodeURIComponent(query.trim())}`);
    }
  }

  return (
    <div className="relative w-full max-w-xs">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search users, buildings, bookings…"
        className="pl-9 h-9 text-sm bg-gray-50 border-gray-200 focus:bg-white w-full"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// AdminTopBar
// ---------------------------------------------------------------------------

type AdminTopBarProps = {
  onMenuClick: () => void;
};

export function AdminTopBar({ onMenuClick }: AdminTopBarProps) {
  const location = useLocation();

  return (
    <header className="bg-white border-b border-gray-200 h-14 px-3 sm:px-6 flex items-center gap-2 sm:gap-4 shrink-0">

      {/* ── Hamburger (mobile only) ────────────────────────────────── */}
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9 shrink-0 md:hidden"
        onClick={onMenuClick}
        aria-label="Open navigation menu"
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* ── Breadcrumb ─────────────────────────────────────────────── */}
      {/* On mobile: flex-1 so it takes remaining space after hamburger */}
      <div className="flex-1 min-w-0">
        <Breadcrumb pathname={location.pathname} />
      </div>

      {/* ── Desktop center search ──────────────────────────────────── */}
      <div className="hidden md:flex flex-1 justify-center max-w-sm">
        <DesktopSearch />
      </div>

      {/* ── Right actions ─────────────────────────────────────────── */}
      <div className="flex items-center gap-1 shrink-0">
        {/* Search icon (mobile) */}
        <div className="md:hidden">
          <MobileSearch />
        </div>
        <NotificationBell />
        <UserMenu />
      </div>

    </header>
  );
}
